<script lang="ts">
import type { GlobalCollectionId, GlobalPrimitiveFieldId, GlobalSingleRelationFieldId, ResolvedMonospacePrimitiveField, ResolvedMonospaceSingleRelationField } from '#layers/data-model';

export interface CollectionFieldSelectProps<IncludePrimitive extends boolean = true, IncludeRelation extends boolean = true, Multiple extends boolean = false> {
	collectionId: GlobalCollectionId;
	includePrimitiveFields?: IncludePrimitive & boolean;
	includeRelationFields?: IncludeRelation & boolean;
	includeConstrainedFields?: boolean;
	includeUnsupportedFields?: boolean;

	filter?: (field: Field<IncludePrimitive, IncludeRelation>) => boolean;

	multiple?: Multiple & boolean;
	clear?: boolean;

	label?: 'apiName' | 'displayName';
	presentationStyle?: 'select' | 'list';
	disabled?: boolean;

	// TODO(hannes): make this dependent on the actual style
	ui?: any;
	variant?: any;
	trailingIcon?: string;
	size?: 'sm' | 'md' | 'lg';
	open?: boolean;
}

export interface CollectionFieldSelectEmits {
	// TODO(hannes): make this dependent on the actual style
	'update:open': (open: boolean) => void;
}

type Field<
	IncludePrimitive extends boolean,
	IncludeRelation extends boolean,
> =
	(IncludePrimitive extends true ? ResolvedMonospacePrimitiveField : never)
	| (IncludeRelation extends true ? ResolvedMonospaceSingleRelationField : never);

export type SingleModelValue<
	IncludePrimitive extends boolean,
	IncludeRelation extends boolean,
	Multiple extends boolean,
> =
	(IncludePrimitive extends true ? GlobalPrimitiveFieldId : never)
	| (IncludeRelation extends true ? GlobalSingleRelationFieldId : never)
	| (Multiple extends false ? null : never);

export type ModelValueBase<
	IncludePrimitive extends boolean,
	IncludeRelation extends boolean,
	Multiple extends boolean,
> = Multiple extends true
	? SingleModelValue<IncludePrimitive, IncludeRelation, Multiple>[]
	: SingleModelValue<IncludePrimitive, IncludeRelation, Multiple>;
</script>

<script setup lang="ts" generic="IncludePrimitive extends boolean = true, IncludeRelation extends boolean = true, Multiple extends boolean = false">
import { reactivePick, useVModel } from '@vueuse/core';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplayNames, useProjectSchemaStore } from '#imports';
import { ensureArray } from '#layers/base/utils/array';
import { getCollectionFields } from '#layers/data-model/utils/schema/collection';

export interface CollectionFieldSelectPresentationProps<Multiple extends boolean = false> {
	multiple?: Multiple & boolean;
	items: any[];
}

interface CollectionFieldSelectSlots {
	// TODO(hannes): base slots of actually selected style
	default: () => any;
	item: (props: { item: any }) => any;
}

type ModelValue = ModelValueBase<IncludePrimitive, IncludeRelation, Multiple>;
const props = withDefaults(defineProps<CollectionFieldSelectProps<IncludePrimitive, IncludeRelation, Multiple>>(), {
	label: 'displayName',
	includePrimitiveFields: true as any,
	includeRelationFields: true as any,
	includeUnsupportedFields: false,
	presentationStyle: 'select',
	clear: true,
	open: undefined,
});

const emit = defineEmits<CollectionFieldSelectEmits>();

defineSlots<CollectionFieldSelectSlots>();

const modelValue = defineModel<ModelValue>({ required: true });
const open = useVModel(props, 'open', emit, {
	passive: (props.open === undefined) as true,
});

const forwarded = reactivePick(props, 'disabled', 'multiple', 'ui');

const { t } = useI18n();
const { getFieldDisplayName } = useDisplayNames();
const { findCollectionById } = useProjectSchemaStore();
const collection = computed(() => findCollectionById(props.collectionId));

const fields = computed(() => collection.value
	? getCollectionFields(collection.value, {
			includePrimitiveFields: props.includePrimitiveFields,
			includeRelationFields: props.includeRelationFields,
			includeConstrainedFields: props.includeConstrainedFields,
			includeUnsupportedFields: props.includeUnsupportedFields,
		})
			.filter((field) => props.filter?.(field as unknown as Field<IncludePrimitive, IncludeRelation>) ?? true)
	: []);

const fieldItems = computed(() => fields.value.map((field) => ({
	id: field.id,
	apiName: field.apiName,
	label: getFieldDisplayName(field),
})).toSorted((a, b) => a.label.localeCompare(b.label)));

const unknownFieldItems = computed(() => ensureArray(modelValue.value)
	?.filter((id) => id && !fieldItems.value.some((item) => item.id === id))
	.map((id) => ({
		id,
		label: t('collection_field_select.unknown_field'),
		icon: 'lucide:triangle-alert',
		ui: { itemLeadingIcon: '!text-warning' },
		class: 'italic',
	}))
	?? []);

const items = computed(() => [...fieldItems.value, ...unknownFieldItems.value]);

function handleClear() {
	if (props.multiple) {
		modelValue.value = [] as unknown as ModelValue;
	}
	else {
		modelValue.value = null as ModelValue;
	}
}
</script>

<template>
	<CollectionFieldSelectList
		v-if="presentationStyle === 'list'"
		v-bind="{ ...forwarded, ...$attrs }"
		v-model="modelValue as any"
		:items
		@clear="handleClear"
	>
		<template v-if="$slots.item" #item="{ item }">
			<slot name="item" :item />
		</template>
	</CollectionFieldSelectList>
	<CollectionFieldSelectDropdown
		v-else
		v-bind="{ ...forwarded, ...$attrs }"
		v-model="modelValue as any"
		v-model:open="open"
		:items
		:clear
		:variant
		:trailing-icon
		:size
		@clear="handleClear"
	>
		<template v-if="!!$slots.default" #default>
			<slot />
		</template>
	</CollectionFieldSelectDropdown>
</template>
